import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { useActionsInstruction, useDestinataires, useInstruction, type InstructionSaisie } from '@/api/hooks';
import { LIBELLE_TYPE_INSTRUCTION } from '@/api/types';
import { Chargement, ConfirmationDialog, ErreurBloc, PageHeader } from '@/components/common';
import { PiecesJointes } from '@/components/pieces-jointes';
import { HistoriqueListe, PrioriteBadge, StatutInstructionBadge } from '@/components/courrier';
import { InstructionChamps, instructionVide } from '@/components/instructions-builder';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Textarea } from '@/components/ui/textarea';
import { dateFr, dateHeureFr } from '@/lib/utils';

type ActionValidation = 'CLOTURER' | 'COMPLEMENT' | 'REAFFECTER' | 'NOUVELLE';
const LIBELLE_VALIDATION: Record<ActionValidation, string> = { CLOTURER: 'Exécutée : clôturer', COMPLEMENT: 'Demander un complément', REAFFECTER: 'Réaffecter', NOUVELLE: 'Clôturer et émettre une nouvelle instruction' };

export function InstructionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useInstruction(id);
  const actions = useActionsInstruction();
  const destinataires = useDestinataires();
  const [dialogue, setDialogue] = useState<'reponse' | 'prisConnaissance' | 'reaffectation' | 'validation' | null>(null);
  const [reponse, setReponse] = useState('');
  const [validation, setValidation] = useState<{ action: ActionValidation; motif: string; echeance: string; responsableId: string; nouvelle: InstructionSaisie }>({ action: 'CLOTURER', motif: '', echeance: '', responsableId: '', nouvelle: instructionVide() });

  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const { instruction: i, etat, actions: a, escaladeA } = data;
  const document = i.courrier ? { lien: `/courrier/${i.courrier.id}`, numero: i.courrier.numero, objet: i.courrier.objet, libelle: 'Courrier' } : i.facture ? { lien: `/factures/${i.facture.id}`, numero: i.facture.numero, objet: `${i.facture.fournisseur} · ${i.facture.objet}`, libelle: 'Facture' } : null;
  const executer = async (fn: () => Promise<unknown>, succes: string) => {
    try {
      await fn();
      toast.success(succes);
      setDialogue(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Action impossible.');
    }
  };
  const valider = async () => {
    const v = validation;
    try {
      const r = await actions.valider.mutateAsync({
        id: i.id,
        action: v.action,
        motif: v.motif.trim() || undefined,
        echeance: v.echeance || undefined,
        responsableId: v.action === 'REAFFECTER' ? v.responsableId || undefined : undefined,
        nouvelle: v.action === 'NOUVELLE' ? { responsableId: v.nouvelle.responsableId, type: v.nouvelle.type, texte: v.nouvelle.texte.trim(), echeance: v.nouvelle.echeance } : undefined,
      });
      toast.success(v.action === 'NOUVELLE' ? `Nouvelle instruction ${r.instruction.reference} émise.` : 'Validation enregistrée.');
      setDialogue(null);
      if (r.instruction.id !== i.id) navigate(`/instructions/${r.instruction.id}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Validation impossible.');
    }
  };
  const validationIncomplete =
    (validation.action === 'COMPLEMENT' && !validation.motif.trim()) ||
    (validation.action === 'REAFFECTER' && !validation.responsableId) ||
    (validation.action === 'NOUVELLE' && (!validation.nouvelle.responsableId || validation.nouvelle.texte.trim().length < 5));

  return (
    <div className="space-y-6">
      <PageHeader
        retour={
          <Link to="/instructions" className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> Registre des instructions
          </Link>
        }
        titre={
          <span className="flex flex-wrap items-center gap-2">
            {i.reference} <StatutInstructionBadge statut={i.statut} /> <PrioriteBadge priorite={i.priorite} />
          </span>
        }
        sousTitre={
          <>
            {LIBELLE_TYPE_INSTRUCTION[i.type]} · {i.responsable ? `${i.responsable.prenom} ${i.responsable.nom}` : '—'} ({i.structure?.code ?? '—'}) · échéance {dateFr(i.echeance)}
          </>
        }
        actions={
          <>
            {a.marquerEnCours && (
              <Button variant="outline" onClick={() => executer(() => actions.enCours.mutateAsync(i.id), 'Instruction marquée en cours.')} disabled={actions.enCours.isPending}>
                Marquer en cours
              </Button>
            )}
            {a.prisConnaissance && (
              <Button variant="outline" onClick={() => setDialogue('prisConnaissance')}>
                Pris connaissance
              </Button>
            )}
            {a.repondre && <Button onClick={() => { setReponse(i.reponse ?? ''); setDialogue('reponse'); }}>Répondre</Button>}
            {a.demanderReaffectation && (
              <Button variant="outline" onClick={() => setDialogue('reaffectation')}>
                Demander une réaffectation
              </Button>
            )}
            {a.valider && (
              <Button onClick={() => { setValidation({ action: i.statut === 'REPONSE' ? 'CLOTURER' : i.demandeReaffectation ? 'REAFFECTER' : 'CLOTURER', motif: '', echeance: '', responsableId: '', nouvelle: instructionVide() }); setDialogue('validation'); }}>
                Valider
              </Button>
            )}
          </>
        }
      />

      {etat.enRetard && (
        <Alert variant="destructive">
          <AlertTitle>{etat.joursRetard} jour(s) de retard</AlertTitle>
          <AlertDescription>{escaladeA ? `Escalade au supérieur hiérarchique : ${escaladeA.nom}.` : 'Rappel envoyé au responsable.'}</AlertDescription>
        </Alert>
      )}
      {!etat.enRetard && etat.rappel && (
        <Alert>
          <AlertTitle>Échéance proche</AlertTitle>
          <AlertDescription>Rappel automatique avant l'échéance du {dateFr(i.echeance)}.</AlertDescription>
        </Alert>
      )}
      {i.demandeReaffectation && i.statut !== 'CLOTUREE' && (
        <Alert>
          <AlertTitle>Réaffectation demandée par le responsable</AlertTitle>
          <AlertDescription>{i.demandeReaffectation}</AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Instruction</CardTitle>
              <CardDescription>
                Émise le {dateHeureFr(i.createdAt)} par {i.auteur ? `${i.auteur.prenom} ${i.auteur.nom}` : '—'}
                {i.titulaireId ? ' (par délégation)' : ''} · {etat.joursEcoules} jour(s) écoulé(s)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p className="whitespace-pre-line text-base">{i.texte}</p>
              {document && (
                <div className="rounded-md bg-muted px-3 py-2">
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">{document.libelle}</span>
                  <div>
                    <Link to={document.lien} className="font-medium text-primary hover:underline">
                      {document.numero}
                    </Link>{' '}
                    · {document.objet}
                  </div>
                </div>
              )}
              {!document && <div className="text-muted-foreground">Instruction hors document (orale ou issue d'une réunion).</div>}
            </CardContent>
          </Card>

          {i.reponse && (
            <Card>
              <CardHeader>
                <CardTitle>Réponse du responsable</CardTitle>
                <CardDescription>{dateHeureFr(i.dateReponse)}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-line text-sm">{i.reponse}</p>
              </CardContent>
            </Card>
          )}

          {i.reaffectations.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Réaffectations ({i.reaffectations.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="divide-y text-sm">
                  {i.reaffectations.map((r, k) => (
                    <li key={k} className="py-2">
                      <div className="flex flex-wrap justify-between gap-2">
                        <span>
                          {r.ancienResponsableNom} → <span className="font-medium">{r.nouveauResponsableNom}</span>
                        </span>
                        <span className="text-xs text-muted-foreground">{dateHeureFr(r.date)} · {r.auteurNom}</span>
                      </div>
                      {r.motif && <div className="text-muted-foreground">{r.motif}</div>}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
          <PiecesJointes objetType="IN" objetId={i.id} typesSuggeres={['RAPPORT', 'COURRIER', 'JUSTIFICATIF', 'AUTRE']} description="Rapport, projet de réponse ou avis joints à votre réponse." />
        </div>
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Suivi</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-muted-foreground">Consultée</span><div>{dateHeureFr(i.dateConsultation)}</div></div>
              <div><span className="text-muted-foreground">Répondue</span><div>{dateHeureFr(i.dateReponse)}</div></div>
              <div><span className="text-muted-foreground">Clôturée</span><div>{dateHeureFr(i.dateCloture)}</div></div>
              <div><span className="text-muted-foreground">Dernière action</span><div>{etat.derniereAction ? `${etat.derniereAction.action} · ${dateFr(etat.derniereAction.date)}` : '—'}</div></div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Historique</CardTitle>
            </CardHeader>
            <CardContent>
              <HistoriqueListe historique={i.historique} />
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={dialogue === 'reponse'} onOpenChange={(o) => !o && setDialogue(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Répondre à l'instruction {i.reference}</DialogTitle>
            <DialogDescription>Compte rendu d'exécution ; la réponse entre dans le parapheur du DG pour validation.</DialogDescription>
          </DialogHeader>
          <Textarea rows={6} value={reponse} onChange={(e) => setReponse(e.target.value)} placeholder="Compte rendu, résultat, difficultés rencontrées, pièces jointes référencées…" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogue(null)}>Fermer</Button>
            <Button disabled={reponse.trim().length < 5 || actions.repondre.isPending} onClick={() => executer(() => actions.repondre.mutateAsync({ id: i.id, texte: reponse.trim() }), 'Réponse transmise au DG.')}>
              Transmettre la réponse
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        ouvert={dialogue === 'prisConnaissance'}
        onOpenChange={(o) => setDialogue(o ? 'prisConnaissance' : null)}
        titre="Prise de connaissance"
        description="Pour une instruction « pour information », la prise de connaissance clôture l'instruction."
        libelle="Confirmer"
        onConfirmer={() => executer(() => actions.prisConnaissance.mutateAsync(i.id), 'Prise de connaissance enregistrée.')}
        chargement={actions.prisConnaissance.isPending}
      />
      <ConfirmationDialog
        ouvert={dialogue === 'reaffectation'}
        onOpenChange={(o) => setDialogue(o ? 'reaffectation' : null)}
        titre="Demander une réaffectation"
        description="La demande est soumise au DG, qui réaffecte ou maintient l'instruction (règle R7)."
        libelle="Demander"
        onConfirmer={(m) => (m.length < 5 ? Promise.resolve(toast.error('Le motif est obligatoire.')) : executer(() => actions.demanderReaffectation.mutateAsync({ id: i.id, motif: m }), 'Demande de réaffectation transmise.'))}
        chargement={actions.demanderReaffectation.isPending}
        champ={{ label: 'Motif (obligatoire)', placeholder: 'Ne relève pas de ma Direction : compétence de…' }}
      />

      <Dialog open={dialogue === 'validation'} onOpenChange={(o) => !o && setDialogue(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Validation · {i.reference}</DialogTitle>
            <DialogDescription>Le DG, son délégataire ou son assistante statue sur la réponse ou sur la demande de réaffectation.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {(Object.keys(LIBELLE_VALIDATION) as ActionValidation[]).map((x) => (
                <Button key={x} type="button" size="sm" variant={validation.action === x ? 'default' : 'outline'} onClick={() => setValidation({ ...validation, action: x })}>
                  {LIBELLE_VALIDATION[x]}
                </Button>
              ))}
            </div>
            {validation.action === 'REAFFECTER' && (
              <div className="space-y-1.5">
                <Label>Nouveau responsable</Label>
                <SelectNatif value={validation.responsableId} onChange={(e) => setValidation({ ...validation, responsableId: e.target.value })}>
                  <option value="">Choisir</option>
                  {(destinataires.data ?? []).filter((d) => d.agentId !== i.responsableId).map((d) => (
                    <option key={d.agentId} value={d.agentId}>
                      {d.nom} · {d.fonction}
                    </option>
                  ))}
                </SelectNatif>
              </div>
            )}
            {validation.action === 'NOUVELLE' ? (
              <div className="space-y-1.5">
                <Label>Nouvelle instruction sur le même document</Label>
                <InstructionChamps valeur={validation.nouvelle} onChange={(nouvelle) => setValidation({ ...validation, nouvelle })} />
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-[1fr_11rem]">
                <div className="space-y-1.5">
                  <Label>{validation.action === 'COMPLEMENT' ? 'Complément demandé (obligatoire)' : validation.action === 'REAFFECTER' ? 'Motif (obligatoire sauf pour le DG)' : 'Commentaire'}</Label>
                  <Textarea rows={3} value={validation.motif} onChange={(e) => setValidation({ ...validation, motif: e.target.value })} />
                </div>
                {validation.action !== 'CLOTURER' && (
                  <div className="space-y-1.5">
                    <Label>Nouvelle échéance</Label>
                    <Input type="date" value={validation.echeance} onChange={(e) => setValidation({ ...validation, echeance: e.target.value })} title={validation.action === 'COMPLEMENT' ? 'J+3 par défaut' : ''} />
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogue(null)}>Fermer</Button>
            <Button onClick={valider} disabled={actions.valider.isPending || validationIncomplete}>
              {LIBELLE_VALIDATION[validation.action]}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
